// src/pages/Admin/PagesPersonnalisees/hooks/usePagesPersonnalisees.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addPagePersonnalisee,
  deletePagePersonnalisee,
  getAllPagesPersonnalisees,
  updatePagePersonnalisee,
  updatePagePersonnaliseeMenuVisibility,
  updatePagePersonnaliseeVisibility,
} from "../../../../../services/pagesPersonnaliseesServices";

import {
  addArticlePage,
  deleteArticlePage,
  getAllArticlesByPage,
  updateArticlePage,
} from "../../../../../services/articlesPagesServices";

import {
  buildPagesStats,
  filterAndSortPages,
  getPageArticles,
  isDemoUser,
  normalizeArticlePayload,
  normalizePagePayload,
  safeJsonParse,
} from "../utils/pagesPersonnaliseesUtils";

const INITIAL_FILTERS = {
  query: "",
  status: "all",
  menu: "all",
  sortOrder: "menu_asc",
};

export default function usePagesPersonnalisees() {
  const [pages, setPages] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [articleSaving, setArticleSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const hydratePagesWithArticles = useCallback(async (pagesList = []) => {
    const safePages = Array.isArray(pagesList) ? pagesList : [];

    const hydratedPages = await Promise.all(
      safePages.map(async (page) => {
        const existingArticles = getPageArticles(page);

        if (existingArticles.length) {
          return {
            ...page,
            articles: existingArticles,
          };
        }

        try {
          const articles = await getAllArticlesByPage(page.id);

          return {
            ...page,
            articles: Array.isArray(articles) ? articles : [],
          };
        } catch (error) {
          console.error(
            `Erreur lors de la récupération des articles de la page ${page.id} :`,
            error
          );

          return {
            ...page,
            articles: [],
          };
        }
      })
    );

    return hydratedPages;
  }, []);

  const refreshPages = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getAllPagesPersonnalisees();
      const safeData = Array.isArray(data) ? data : [];

      const hydratedPages = await hydratePagesWithArticles(safeData);

      setPages(hydratedPages);

      return hydratedPages;
    } catch (error) {
      console.error("Erreur lors de la récupération des pages :", error);
      toast.error("Erreur lors de la récupération des pages personnalisées.");

      return [];
    } finally {
      setLoading(false);
    }
  }, [hydratePagesWithArticles]);

  useEffect(() => {
    loadUserMode();
    refreshPages();
  }, [loadUserMode, refreshPages]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const filteredPages = useMemo(
    () => filterAndSortPages(pages, filters),
    [pages, filters]
  );

  const stats = useMemo(
    () => buildPagesStats(pages, filteredPages),
    [pages, filteredPages]
  );

  const syncSelectedPage = useCallback(
    async (pageId) => {
      const updatedPages = await refreshPages();
      const updatedPage = updatedPages.find(
        (page) => Number(page.id) === Number(pageId)
      );

      if (updatedPage) {
        setSelectedPage(updatedPage);
      }

      return updatedPage;
    },
    [refreshPages]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedPage(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (page) => {
      if (guardDemoMode()) return;

      setSelectedPage(page);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((page) => {
    setSelectedPage(page);
    setDrawerMode("details");
  }, []);

  const openArticlesDrawer = useCallback((page) => {
    setSelectedPage(page);
    setDrawerMode("articles");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedPage(null);
  }, []);

  const createPage = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizePagePayload(formData);
        const createdPage = await addPagePersonnalisee(payload);

        if (!createdPage) {
          throw new Error("Création impossible.");
        }

        toast.success("Page personnalisée créée avec succès.");
        closeDrawer();
        await refreshPages();
      } catch (error) {
        console.error("Erreur lors de la création de la page :", error);
        toast.error("Erreur lors de la création de la page.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshPages]
  );

  const savePage = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizePagePayload(formData);
        const updatedPage = await updatePagePersonnalisee(payload);

        if (!updatedPage) {
          throw new Error("Modification impossible.");
        }

        toast.success("Page personnalisée modifiée avec succès.");
        closeDrawer();
        await refreshPages();
      } catch (error) {
        console.error("Erreur lors de la modification de la page :", error);
        toast.error("Erreur lors de la modification de la page.");
      } finally {
        setSaving(false);
      }
    },
    [closeDrawer, guardDemoMode, refreshPages]
  );

  const togglePageVisibility = useCallback(
    async (page) => {
      if (guardDemoMode()) return;

      try {
        const response = await updatePagePersonnaliseeVisibility(
          page.id,
          !Boolean(page.actif)
        );

        if (!response) {
          throw new Error("Mise à jour visibilité impossible.");
        }

        toast.success(
          Boolean(page.actif)
            ? "Page désactivée avec succès."
            : "Page activée avec succès."
        );

        await refreshPages();
      } catch (error) {
        console.error("Erreur lors du changement de visibilité :", error);
        toast.error("Erreur lors du changement de visibilité.");
      }
    },
    [guardDemoMode, refreshPages]
  );

  const togglePageMenuVisibility = useCallback(
    async (page) => {
      if (guardDemoMode()) return;

      try {
        const response = await updatePagePersonnaliseeMenuVisibility(
          page.id,
          !Boolean(page.afficher_menu)
        );

        if (!response) {
          throw new Error("Mise à jour affichage menu impossible.");
        }

        toast.success(
          Boolean(page.afficher_menu)
            ? "Page masquée du menu."
            : "Page affichée dans le menu."
        );

        await refreshPages();
      } catch (error) {
        console.error("Erreur lors du changement d'affichage menu :", error);
        toast.error("Erreur lors du changement d'affichage menu.");
      }
    },
    [guardDemoMode, refreshPages]
  );

  const askDelete = useCallback(
    (pageId) => {
      if (guardDemoMode()) return;
      setDeleteCandidateId(pageId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (pageId) => {
      if (guardDemoMode()) return;

      try {
        const response = await deletePagePersonnalisee(pageId);

        if (!response) {
          throw new Error("Suppression impossible.");
        }

        toast.success("Page personnalisée supprimée avec succès.");
        setDeleteCandidateId(null);
        await refreshPages();
      } catch (error) {
        console.error("Erreur lors de la suppression de la page :", error);
        toast.error("Erreur lors de la suppression de la page.");
      }
    },
    [guardDemoMode, refreshPages]
  );

  const addArticleToPage = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setArticleSaving(true);

      try {
        const payload = normalizeArticlePayload(formData);
        const createdArticle = await addArticlePage(payload);

        if (!createdArticle) {
          throw new Error("Création article impossible.");
        }

        toast.success("Article ajouté avec succès.");
        await syncSelectedPage(payload.page_id);
      } catch (error) {
        console.error("Erreur lors de l'ajout de l'article :", error);
        toast.error("Erreur lors de l'ajout de l'article.");
      } finally {
        setArticleSaving(false);
      }
    },
    [guardDemoMode, syncSelectedPage]
  );

  const updateArticleFromPage = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setArticleSaving(true);

      try {
        const payload = normalizeArticlePayload(formData);
        const updatedArticle = await updateArticlePage(payload);

        if (!updatedArticle) {
          throw new Error("Modification article impossible.");
        }

        toast.success("Article modifié avec succès.");
        await syncSelectedPage(payload.page_id);
      } catch (error) {
        console.error("Erreur lors de la modification de l'article :", error);
        toast.error("Erreur lors de la modification de l'article.");
      } finally {
        setArticleSaving(false);
      }
    },
    [guardDemoMode, syncSelectedPage]
  );

  const deleteArticleFromPage = useCallback(
    async ({ pageId, articleId }) => {
      if (guardDemoMode()) return;

      setArticleSaving(true);

      try {
        const response = await deleteArticlePage(articleId);

        if (!response) {
          throw new Error("Suppression article impossible.");
        }

        toast.success("Article supprimé avec succès.");
        await syncSelectedPage(pageId);
      } catch (error) {
        console.error("Erreur lors de la suppression de l'article :", error);
        toast.error("Erreur lors de la suppression de l'article.");
      } finally {
        setArticleSaving(false);
      }
    },
    [guardDemoMode, syncSelectedPage]
  );

  return {
    loading,
    saving,
    articleSaving,
    filters,
    setFilter,
    pages,
    filteredPages,
    selectedPage,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshPages,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      openArticlesDrawer,
      closeDrawer,
      createPage,
      updatePage: savePage,
      togglePageVisibility,
      togglePageMenuVisibility,
      askDelete,
      cancelDelete,
      confirmDelete,
      addArticleToPage,
      updateArticleFromPage,
      deleteArticleFromPage,
    },
  };
}